import type React from "react"
import { useEffect, useState } from "react"
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import LanguageSelector from "../components/LanguageSelector"
import { useAuth } from "../contexts/AuthContext"
import { useCryptoNavigation } from "../App"
import { useTranslation } from "react-i18next"
import { useColorMode } from "../theme/colorMode"
import logoLoginDark from "../assets/images/Horse Logo.png"

// Configuração de debug - SEMPRE FALSE em produção
const DEBUG_MODE = false; // ❌ Manter sempre FALSE em produção

// Função de log seguro - só funciona se DEBUG_MODE = true
const secureLog = (message: string, data?: any) => {
  if (DEBUG_MODE && process.env.NODE_ENV === 'development') {
    console.log(message, data);
  }
};

export default function Login() {
  const { t } = useTranslation()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()
  const { navigateToPage } = useCryptoNavigation()
  const { mode } = useColorMode()
  // Ver o que se digitou é a defesa mais simples contra errar a senha — e foi
  // exatamente o que fez um login parecer "banco errado".
  const [mostrarSenha, setMostrarSenha] = useState(false)

  // ✅ VERSÃO SEGURA: Remove logs desnecessários e dados sensíveis
  useEffect(() => {
    // Limpa dados sensíveis antigos se existirem
    const sensitiveKeys = [
      'lastPasswordChangeAttempt',
      'lastPasswordChangePayload',
      'lastPasswordChangeToken',
      'lastPasswordChangeError',
      'lastPasswordChangeResponse',
      'lastPasswordChangeSuccess'
    ];
    
    sensitiveKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
    });
    
    secureLog('🧹 Dados sensíveis limpos do localStorage');
  }, []);

  // ✅ Função segura para determinar mensagem de erro
  const getErrorMessage = (error: any): string => {
    // Log mínimo apenas em desenvolvimento
    secureLog('Processando erro de login');
    
    // Verificações de erro sem expor dados sensíveis
    if (error?.code === 'NETWORK_ERROR' || error?.code === 'ERR_NETWORK') {
      return t("login.errorServerUnavailable");
    }
    
    if (error?.response) {
      const status = error.response.status;
      
      // Log seguro apenas do status
      secureLog(`Status HTTP: ${status}`);
      
      switch (status) {
        case 401:
        case 404:
          return t("login.errorInvalidCredentials");
        case 403:
          return t("login.errorAccessDenied");
        case 500:
        case 502:
        case 503:
        case 504:
          return t("login.errorServerUnavailable");
        default:
          return t("login.errorGeneric");
      }
    }
    
    if (error?.message && (
      error.message.includes('timeout') ||
      error.message.includes('Network Error') ||
      error.message.includes('Failed to fetch')
    )) {
      return t("login.errorServerUnavailable");
    }
    
    return t("login.errorInvalidCredentials");
  };

  // ✅ Função de limpeza de dados sensíveis
  const clearSensitiveData = () => {
    setUsername("");
    setPassword("");
    // Força garbage collection das variáveis
    setTimeout(() => {
      // Limpa qualquer referência residual
      if (typeof username !== 'undefined') setUsername("");
      if (typeof password !== 'undefined') setPassword("");
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    try {
      secureLog('Iniciando processo de login');
      
      // ✅ Executa login sem expor dados
      const loginResponse = await login(username, password)
      
      // ✅ Log seguro apenas do sucesso
      secureLog('Login realizado com sucesso');
      
      // ✅ Limpa dados sensíveis imediatamente após uso
      clearSensitiveData();
      
      // Verifica tenant sem expor dados
      const selectedTenantId = localStorage.getItem('selectedTenantId');
      if (selectedTenantId) {
        secureLog('Tenant configurado corretamente');
      }
      
      // Navega para o dashboard
      navigateToPage('dashboard')
      
    } catch (err) {
      secureLog('Erro durante login processado');
      
      // ✅ Processa erro sem expor dados sensíveis
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      
      // ✅ Limpa dados sensíveis em caso de erro também
      clearSensitiveData();
      
    } finally {
      setLoading(false)
      secureLog('Processo de login finalizado')
    }
  }

  // ✅ Handlers seguros para mudanças nos campos
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value)
    if (error) {
      setError("")
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    if (error) {
      setError("")
    }
  }

  // ✅ Limpa dados quando componente é desmontado
  useEffect(() => {
    return () => {
      clearSensitiveData();
    };
  }, []);

  // ✅ Limpa dados quando a janela é fechada
  useEffect(() => {
    const handleBeforeUnload = () => {
      clearSensitiveData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearSensitiveData();
    };
  }, []);

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          
          {/* Trocar o idioma ANTES de entrar: quem não lê o idioma atual não
              consegue nem interpretar a mensagem de erro do login. O app
              Flutter já tinha isto na tela de login; o admin, não. */}
          <Box sx={{ alignSelf: "flex-end", mt: -2, mb: -1 }}>
            <LanguageSelector />
          </Box>

          <img
            src={mode === "dark" ? logoLoginDark : "/logo_horsetech.png"}
            alt="Logo"
            style={{ width: "100%", maxWidth: 240, height: "auto", marginBottom: 16 }}
          />

          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            {t("login.title")}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          )}          
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: "100%" }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label={t("login.user")}
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={handleUsernameChange}
              type="text"
              error={!!error}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t("login.password")}
              type={mostrarSenha ? "text" : "password"}
              id="password"
              value={password}
              onChange={handlePasswordChange}
              error={!!error}
              disabled={loading}
              // ✅ Configurações seguras para campo de senha
              autoComplete="current-password"
              inputProps={{
                autoComplete: "current-password"
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setMostrarSenha((v) => !v)}
                      edge="end"
                      disabled={loading}
                      aria-label={t(
                        mostrarSenha ? "login.hidePassword" : "login.showPassword",
                      )}
                      // Fora da ordem de tabulação: quem navega por teclado
                      // quer ir do campo para o botão de entrar.
                      tabIndex={-1}
                    >
                      {mostrarSenha ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button 
              type="submit" 
              fullWidth 
              variant="contained" 
              sx={{ mt: 3, mb: 2 }} 
              disabled={loading}
            >
              {loading ? t("login.signingIn") : t("login.signIn")}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}